import type { BodyRegion } from '../catalog/muscles.js';
import { regionForMuscle, type MovementPattern, type Muscle } from '../catalog/muscles.js';

/**
 * Banderas rojas (docs/SECURITY_PRIVACY.md §6.1, requisito §64).
 *
 * Esto es lo que convierte una regla escrita en una regla que se cumple. El
 * documento decia desde la Fase 0 que con una bandera roja activa esta
 * «prohibido» prescribir, pero no habia ni una linea de codigo detras: el motor
 * de cargas ni siquiera recibia el dato.
 *
 * Dos limites que no se cruzan:
 *
 * 1. **No se diagnostica.** Aqui no se nombra ninguna patologia ni se aventura
 *    una causa. Se detiene la recomendacion y se recomienda una valoracion
 *    profesional; nada mas.
 * 2. **Los sintomas se marcan, no se escriben.** La entrada son opciones
 *    tipificadas. Interpretar texto libre para decidir si alguien deja de
 *    entrenar seria justo el tipo de inferencia que este sistema no debe hacer.
 */

/** Sintomas que el usuario puede marcar en el check-in. Lista cerrada. */
export const SYMPTOM_CODES = [
  'chest_pain',
  'breathing_difficulty',
  'neuro_symptoms',
  'fainting',
  'joint_pain',
] as const;

export type SymptomCode = (typeof SYMPTOM_CODES)[number];

/**
 * Cómo se leen las opciones (revisadas el 23/09/2026, encargo §8).
 *
 * Antes decían «Dificultad para respirar» —y cualquiera se queda sin aire al
 * apretar una serie— y «Mareo, hormigueo o pérdida de fuerza», que mezclaba un
 * mareo leve con una pérdida de fuerza de un lado del cuerpo. Las dos son
 * urgencias en su forma grave y ruido en la leve, así que la opción describe la
 * forma grave. Los códigos no cambian: son datos guardados.
 */
export const SYMPTOM_LABELS: Record<SymptomCode, string> = {
  chest_pain: 'Dolor, presión u opresión en el pecho',
  breathing_difficulty: 'Falta de aire desproporcionada o que no cede al parar',
  neuro_symptoms: 'Pérdida brusca de fuerza, hormigueo o dificultad para hablar, en un lado del cuerpo',
  fainting: 'Desmayo o pérdida de conocimiento',
  joint_pain: 'Dolor articular intenso, nuevo o que va a más',
};

/**
 * Síntomas que son una POSIBLE URGENCIA en cuanto se marcan una sola vez. No se
 * espera a una tendencia ni a completar preguntas: el coste de equivocarse por
 * exceso es una sesión perdida, y el de equivocarse por defecto no es
 * comparable (encargo del 23/09/2026, §8).
 */
const URGENTES: ReadonlySet<SymptomCode> = new Set([
  'chest_pain',
  'breathing_difficulty',
  'neuro_symptoms',
  'fainting',
]);

export type RedFlagSeverity = 'halt' | 'advise';

export interface RedFlag {
  code: string;
  severity: RedFlagSeverity;
  /** `null` = afecta a toda la sesion; si no, solo a esa region. */
  region: BodyRegion | null;
  /**
   * Posible urgencia: se deja de entrenar y se pide ayuda YA. No hay sesión
   * que ofrecer a la vez, ni siquiera «sin cargas» (encargo §6: ante bloqueo
   * de seguridad no se devuelve una sesión normal ejecutable).
   */
  urgent: boolean;
  /** Texto para el usuario. Nunca nombra una patologia. */
  message: string;
}

export interface RedFlagCheckin {
  forDate: string;
  sleepHours: number;
  reportedSymptoms?: readonly SymptomCode[];
  sorenessAreas: readonly {
    region: BodyRegion;
    intensity: number;
    kind: 'doms' | 'stiffness' | 'discomfort';
  }[];
}

export interface RedFlagInput {
  /** Del mas reciente al mas antiguo. */
  recentCheckins: readonly RedFlagCheckin[];
  /**
   * Hoy, en local. Hasta el 23/09/2026 no existía: se miraba el ÚLTIMO check-in
   * fuera de cuando fuera, así que un síntoma marcado hace diez días seguía
   * bloqueando mientras no se hiciera otro check-in. Un síntoma describe el
   * momento en que se marca, no el estado actual.
   */
  today: string;
}

export const RED_FLAG_THRESHOLDS = {
  /** Check-ins consecutivos con molestia intensa en la misma articulacion. */
  jointPainCheckins: 3,
  jointPainIntensity: 4,
  /**
   * Días que puede tener el check-in más reciente para que la persistencia
   * cuente. Más viejo, describe otro momento: no bloquea, y el siguiente
   * check-in vuelve a evaluar desde cero. Convención declarada.
   */
  persistenceFreshnessDays: 7,
  /** Media de sueño por debajo de la cual se avisa, con su ventana minima. */
  sleepHoursFloor: 5,
  sleepWindow: 7,
} as const;

const AVISO_PROFESIONAL =
  'Consulta con un profesional sanitario antes de seguir entrenando. La aplicación no valora síntomas.';

/**
 * Qué hacer ante cada posible urgencia. Acciones, no diagnósticos: la
 * aplicación no sabe qué es, y por eso no lo nombra.
 *
 * El número: 112 funciona en toda la Unión Europea y es el de España. No se
 * deduce el país del idioma ni del huso: se dice «el número de emergencias de
 * donde estés» y se da el 112 como el de la UE.
 */
const ACCION_URGENTE: Record<string, string> = {
  chest_pain:
    'Deja de entrenar ahora. Si la presión en el pecho no cede en unos minutos, o viene con falta de aire, sudor frío, náuseas o dolor que se extiende al brazo, la mandíbula o la espalda, llama al número de emergencias de donde estés (112 en toda la UE). No esperes a ver si se pasa.',
  breathing_difficulty:
    'Deja de entrenar ahora. Si la falta de aire no cede en reposo o va a más, llama al número de emergencias de donde estés (112 en toda la UE).',
  neuro_symptoms:
    'Deja de entrenar ahora. Una pérdida brusca de fuerza, hormigueo o dificultad para hablar en un lado del cuerpo es motivo para llamar YA al número de emergencias de donde estés (112 en toda la UE).',
  fainting:
    'No entrenes hoy. Tras un desmayo, busca atención médica antes de volver a hacer esfuerzo; si hubo golpe, dolor en el pecho o no te recuperas del todo, llama al número de emergencias de donde estés (112 en toda la UE).',
};

/** Articulación del mapa de dolor → nombre para el usuario. */
const NOMBRE_ZONA: Partial<Record<BodyRegion, string>> = {
  shoulders_front: 'hombro',
  elbow: 'codo',
  wrist: 'muñeca',
  neck: 'cuello',
  lower_back: 'zona lumbar',
  hip: 'cadera',
  knee: 'rodilla',
  ankle: 'tobillo',
};

function diasEntre(desde: string, hasta: string): number {
  return Math.round(
    (Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / 86_400_000,
  );
}

/** Regiones con dolor articular en un check-in, UNA vez cada una aunque se repita. */
function regionesConDolor(checkin: RedFlagCheckin, intensidadMinima: number): Set<BodyRegion> {
  return new Set(
    checkin.sorenessAreas
      // Solo `discomfort`: unas agujetas fuertes no son una molestia articular.
      .filter((zona) => zona.kind === 'discomfort' && zona.intensity >= intensidadMinima)
      .map((zona) => zona.region),
  );
}

export function detectRedFlags(input: RedFlagInput): RedFlag[] {
  const banderas: RedFlag[] = [];
  const checkins = input.recentCheckins;
  const ultimo = checkins[0];
  // Solo es «hoy» el check-in de hoy. Los síntomas de otro día no se arrastran.
  const deHoy = ultimo !== undefined && ultimo.forDate === input.today ? ultimo : undefined;

  // --- Posibles urgencias marcadas hoy --------------------------------------
  for (const sintoma of deHoy?.reportedSymptoms ?? []) {
    if (!URGENTES.has(sintoma)) continue;
    banderas.push({
      code: sintoma,
      severity: 'halt',
      region: null,
      urgent: true,
      message: ACCION_URGENTE[sintoma] ?? AVISO_PROFESIONAL,
    });
  }

  // --- Dolor articular marcado HOY --------------------------------------------
  // No se esperan tres check-ins: el dolor intenso actual se atiende hoy
  // (encargo §3). Hasta el 23/09/2026 `joint_pain` no tenía rama propia, así
  // que marcarlo no hacía nada mientras la pantalla prometía lo contrario.
  if (deHoy?.reportedSymptoms?.includes('joint_pain') === true) {
    const zonas = regionesConDolor(deHoy, 1);
    if (zonas.size === 0) {
      // Sin dónde, no se puede acotar: no se prescribe nada que presuponga que
      // la zona no está afectada.
      banderas.push({
        code: 'joint_pain_unlocated',
        severity: 'halt',
        region: null,
        urgent: false,
        message:
          'Has marcado dolor articular, pero no dónde. Señala la zona en el check-in: hasta entonces no te propongo cargas. Si es intenso, nuevo o va a más, consulta con un profesional sanitario.',
      });
    } else {
      for (const region of zonas) {
        banderas.push({
          code: 'current_joint_pain',
          severity: 'halt',
          region,
          urgent: false,
          message: `Hoy no te propongo nada que cargue la zona que has marcado (${NOMBRE_ZONA[region] ?? 'esa zona'}). Si el dolor es intenso, nuevo o va a más, consulta con un profesional sanitario.`,
        });
      }
    }
  }

  // --- Molestia articular intensa y sostenida -------------------------------
  // Cuenta CHECK-INS, no entradas: la misma región marcada dos veces en un
  // check-in es una vez. Y solo si el más reciente está vigente: tres registros
  // de hace un mes no describen hoy, y el siguiente check-in lo resuelve.
  const ventana = checkins.slice(0, RED_FLAG_THRESHOLDS.jointPainCheckins);
  const vigente =
    ultimo !== undefined &&
    diasEntre(ultimo.forDate, input.today) <= RED_FLAG_THRESHOLDS.persistenceFreshnessDays;
  if (vigente && ventana.length === RED_FLAG_THRESHOLDS.jointPainCheckins) {
    const persistentes = new Map<BodyRegion, number>();
    for (const checkin of ventana) {
      for (const region of regionesConDolor(checkin, RED_FLAG_THRESHOLDS.jointPainIntensity)) {
        persistentes.set(region, (persistentes.get(region) ?? 0) + 1);
      }
    }
    const yaMarcadas = new Set(banderas.map((b) => b.region));
    for (const [region, veces] of persistentes) {
      if (veces < RED_FLAG_THRESHOLDS.jointPainCheckins || yaMarcadas.has(region)) continue;
      banderas.push({
        code: 'persistent_joint_pain',
        severity: 'halt',
        region,
        urgent: false,
        message: `Llevas ${veces} check-ins seguidos con molestia intensa en la misma zona. ${AVISO_PROFESIONAL}`,
      });
    }
  }

  // --- Deficit de sueño sostenido -------------------------------------------
  const sueño = checkins.slice(0, RED_FLAG_THRESHOLDS.sleepWindow);
  if (vigente && sueño.length === RED_FLAG_THRESHOLDS.sleepWindow) {
    const media = sueño.reduce((suma, c) => suma + c.sleepHours, 0) / sueño.length;
    if (media < RED_FLAG_THRESHOLDS.sleepHoursFloor) {
      banderas.push({
        code: 'persistent_sleep_deficit',
        severity: 'advise',
        region: null,
        urgent: false,
        // Avisa, no detiene: dormir poco no es un sintoma de alarma.
        message: `Llevas una semana durmiendo menos de ${RED_FLAG_THRESHOLDS.sleepHoursFloor} h de media. Conviene bajar la carga y priorizar el descanso.`,
      });
    }
  }

  return banderas;
}

/** ¿Hay una posible urgencia? Entonces no se ofrece ninguna sesión. */
export function hasUrgentFlag(flags: readonly RedFlag[]): boolean {
  return flags.some((flag) => flag.urgent);
}

/** ¿Alguna bandera detiene la sesion entera? */
export function haltsTraining(flags: readonly RedFlag[]): boolean {
  return flags.some((flag) => flag.severity === 'halt' && flag.region === null);
}

/** Regiones con el entrenamiento detenido, aunque el resto pueda seguir. */
export function haltedRegions(flags: readonly RedFlag[]): BodyRegion[] {
  return flags
    .filter((flag) => flag.severity === 'halt' && flag.region !== null)
    .map((flag) => flag.region as BodyRegion);
}

/**
 * Articulaciones y los patrones de movimiento que las cargan.
 *
 * Hace falta porque las regiones del mapa corporal no son todas musculares: a
 * `knee` no apunta ningun musculo, asi que una molestia de rodilla no bloqueaba
 * NADA si solo se miraban los musculos del ejercicio. Lo detecto un test de
 * integracion, no el razonamiento.
 *
 * Se mapea por patron y no por musculo a proposito: lo que carga una rodilla es
 * flexionarla bajo carga, no que el cuadriceps participe. Asi un curl femoral
 * queda bloqueado y una elevacion de gemelos sentado, no.
 */
const ARTICULACION_A_PATRONES: Partial<Record<BodyRegion, readonly MovementPattern[]>> = {
  knee: ['squat', 'lunge', 'isolation_knee_ext', 'isolation_knee_flex'],
  // El hombro del mapa de DOLOR (23/09/2026): la articulación, no el deltoides.
  // Un press de banca la carga aunque su músculo principal sea el pectoral.
  shoulders_front: [
    'horizontal_push',
    'vertical_push',
    'isolation_chest_fly',
    'isolation_shoulder_abd',
  ],
  hip: ['squat', 'hinge', 'lunge', 'isolation_hip'],
  ankle: ['squat', 'lunge', 'isolation_calf'],
  elbow: [
    'horizontal_push',
    'vertical_push',
    'horizontal_pull',
    'vertical_pull',
    'isolation_elbow_flex',
    'isolation_elbow_ext',
  ],
  wrist: [
    'horizontal_push',
    'vertical_push',
    'isolation_elbow_flex',
    'isolation_elbow_ext',
  ],
  lower_back: ['squat', 'hinge', 'horizontal_pull'],
  neck: ['vertical_push'],
};

export interface ExerciseSafetyProfile {
  muscles: readonly Muscle[];
  pattern: MovementPattern;
}

/**
 * ¿Hay que dejar de prescribir este ejercicio?
 *
 * Se bloquea por tres vias: bandera sistemica, region muscular detenida, o
 * articulacion detenida cuyo patron carga este ejercicio.
 */
export function blocksExercise(
  flags: readonly RedFlag[],
  exercise: ExerciseSafetyProfile,
): boolean {
  if (haltsTraining(flags)) return true;

  const detenidas = new Set(haltedRegions(flags));
  if (detenidas.size === 0) return false;

  if (exercise.muscles.some((musculo) => detenidas.has(regionForMuscle(musculo)))) return true;

  for (const region of detenidas) {
    const patrones = ARTICULACION_A_PATRONES[region];
    if (patrones?.includes(exercise.pattern) === true) return true;
  }
  return false;
}

/** Codigos para `assessDeload`, que solo necesita saber si hay alguna. */
export function haltingCodes(flags: readonly RedFlag[]): string[] {
  return flags.filter((flag) => flag.severity === 'halt').map((flag) => flag.code);
}
